import { validatePageTrigger } from './page-trigger'

// In-house surveys (#25344). Pure: the API validates with these before it writes, and the admin
// form (#25351) and the survey dialog (#25349) can run the same checks in the browser.
//
//   validateSurveyContent(survey)            -> string[]   check a definition before saving
//   validateSurveyAnswers(questions, input)  -> { answers, errors }   check one submission
//   toHubspotProperties(questions, answers)  -> Record<property, value>   mapped answers only

export const SURVEY_QUESTION_TYPES = ['singleChoice', 'multipleChoice', 'rating', 'text'] as const
export type SurveyQuestionType = (typeof SURVEY_QUESTION_TYPES)[number]

/**
 * `on-login`: the app opens the survey at startup when the user is eligible.
 * `manual`: the app opens it from code with `key` (the replacement for Userflow's `triggerFlow`).
 * `page-view`: the app opens it when the user opens a page that matches `pathPattern` (#25610).
 */
export const SURVEY_TRIGGERS = ['on-login', 'manual', 'page-view'] as const
export type SurveyTrigger = (typeof SURVEY_TRIGGERS)[number]

export const SURVEY_LIMITS = {
  title: 200,
  description: 2000,
  prompt: 500,
  questions: 20,
  options: 20,
  optionValue: 100,
  optionLabel: 200,
  id: 64,
  text: 2000,
  ratingMax: 10
} as const

export interface SurveyOption {
  /** Stored in the answer, and sent to HubSpot. For a mapped question, use the HubSpot option's internal value. */
  value: string
  label: string
}

export interface SurveyQuestion {
  /** Stable within the survey. Answers refer to it, so never reuse an id for a different question. */
  id: string
  type: SurveyQuestionType
  prompt: string
  required: boolean
  /** singleChoice and multipleChoice only. */
  options?: SurveyOption[] | null
  /** rating only. Whole numbers; defaults 1 and 5. */
  scaleMin?: number | null
  scaleMax?: number | null
  minLabel?: string | null
  maxLabel?: string | null
  /** The HubSpot contact property this answer is written to. Unset: the answer is stored only. */
  hubspotProperty?: string | null
}

/** The content of a survey that `validateSurveyContent` checks. */
export interface SurveyContent {
  title: string
  description?: string | null
  trigger: SurveyTrigger
  /** Required for `manual`, ignored otherwise. */
  key?: string | null
  /** Required for `page-view`, ignored otherwise: a path with `*` wildcards. */
  pathPattern?: string | null
  questions: SurveyQuestion[]
}

/** One answer as stored. Exactly one of the value fields is set, by question type. */
export interface SurveyAnswer {
  questionId: string
  /** singleChoice (one value) and multipleChoice (one or more). */
  choices?: string[]
  rating?: number
  text?: string
}

/**
 * Contact properties that the CRM sync owns. A survey must not overwrite who the contact is or
 * how the sync matches it.
 */
export const RESERVED_HUBSPOT_PROPERTIES: ReadonlySet<string> = new Set([
  'email',
  'firstname',
  'lastname',
  'phone',
  'jobtitle',
  'lifecyclestage',
  'hs_object_id',
  'hubspot_owner_id',
  'associatedcompanyid',
  'sync_source',
  'user_type',
  'paintscout_user_id',
  'paintscout_company_id',
  'crm_add_on_status__user_',
  'paintscout_subscription_status__user_',
  'last_login_date__user_',
  'user_activation_date'
])

const KEY_PATTERN = /^[a-z0-9][a-z0-9_-]*$/
const HUBSPOT_PROPERTY_PATTERN = /^[a-z][a-z0-9_]*$/
const HUBSPOT_PROPERTY_MAX = 100

const isBlank = (value: unknown) => typeof value !== 'string' || value.trim() === ''

export function getRatingScale(question: Pick<SurveyQuestion, 'scaleMin' | 'scaleMax'>): { min: number; max: number } {
  return { min: question.scaleMin ?? 1, max: question.scaleMax ?? 5 }
}

function validateQuestion(question: SurveyQuestion, at: string): string[] {
  const errors: string[] = []
  if (!SURVEY_QUESTION_TYPES.includes(question?.type)) {
    return [`${at}: unknown type "${question?.type}"`]
  }
  if (isBlank(question.prompt)) errors.push(`${at}: prompt is required`)
  else if (question.prompt.length > SURVEY_LIMITS.prompt) errors.push(`${at}: prompt is too long`)

  const isChoice = question.type === 'singleChoice' || question.type === 'multipleChoice'
  const options = question.options ?? []
  if (isChoice) {
    if (options.length < 2) errors.push(`${at}: needs at least 2 options`)
    if (options.length > SURVEY_LIMITS.options) errors.push(`${at}: at most ${SURVEY_LIMITS.options} options`)
    const values = new Set<string>()
    options.forEach((option, i) => {
      if (isBlank(option?.value) || option.value.length > SURVEY_LIMITS.optionValue) {
        errors.push(`${at}.options[${i}]: value is required (at most ${SURVEY_LIMITS.optionValue} characters)`)
      } else if (values.has(option.value)) {
        errors.push(`${at}.options[${i}]: duplicate value "${option.value}"`)
      } else {
        values.add(option.value)
      }
      if (option?.value?.includes(';')) errors.push(`${at}.options[${i}]: value cannot contain ";"`)
      if (isBlank(option?.label) || option.label.length > SURVEY_LIMITS.optionLabel) {
        errors.push(`${at}.options[${i}]: label is required (at most ${SURVEY_LIMITS.optionLabel} characters)`)
      }
    })
  } else if (options.length > 0) {
    errors.push(`${at}: only choice questions have options`)
  }

  if (question.type === 'rating') {
    const { min, max } = getRatingScale(question)
    if (!Number.isInteger(min) || !Number.isInteger(max) || min < 0 || max > SURVEY_LIMITS.ratingMax || min >= max) {
      errors.push(`${at}: the rating scale must be whole numbers with 0 <= min < max <= ${SURVEY_LIMITS.ratingMax}`)
    }
  }

  const property = question.hubspotProperty
  if (property != null && property !== '') {
    if (!HUBSPOT_PROPERTY_PATTERN.test(property) || property.length > HUBSPOT_PROPERTY_MAX) {
      errors.push(`${at}: "${property}" is not a HubSpot property name (lowercase letters, digits and _)`)
    } else if (RESERVED_HUBSPOT_PROPERTIES.has(property)) {
      errors.push(`${at}: the CRM sync owns "${property}"; map the answer to another property`)
    }
  }
  return errors
}

/** Problems an admin must fix before saving a survey. An empty array means the survey is valid. */
export function validateSurveyContent(survey: SurveyContent): string[] {
  const errors: string[] = []
  if (isBlank(survey?.title)) errors.push('title is required')
  else if (survey.title.length > SURVEY_LIMITS.title) errors.push('title is too long')
  if (survey?.description && survey.description.length > SURVEY_LIMITS.description) {
    errors.push('description is too long')
  }

  if (!SURVEY_TRIGGERS.includes(survey?.trigger)) {
    errors.push(`unknown trigger "${survey?.trigger}"`)
  } else if (
    survey.trigger === 'manual' &&
    (!survey.key || !KEY_PATTERN.test(survey.key) || survey.key.length > SURVEY_LIMITS.id)
  ) {
    errors.push('a manual survey needs a key (lowercase letters, digits, - and _)')
  } else {
    const triggerError = validatePageTrigger(survey)
    if (triggerError) errors.push(triggerError)
  }

  const questions = survey?.questions ?? []
  if (questions.length === 0) errors.push('add at least one question')
  if (questions.length > SURVEY_LIMITS.questions) errors.push(`at most ${SURVEY_LIMITS.questions} questions`)

  const ids = new Set<string>()
  const properties = new Map<string, number>()
  questions.forEach((question, i) => {
    const at = `questions[${i}]`
    if (isBlank(question?.id) || question.id.length > SURVEY_LIMITS.id) {
      errors.push(`${at}: id is required (at most ${SURVEY_LIMITS.id} characters)`)
    } else if (ids.has(question.id)) {
      errors.push(`${at}: duplicate id "${question.id}"`)
    } else {
      ids.add(question.id)
    }
    errors.push(...validateQuestion(question, at))
    const property = question?.hubspotProperty
    if (property) {
      if (properties.has(property)) {
        errors.push(`${at}: questions[${properties.get(property)}] already writes "${property}"`)
      } else {
        properties.set(property, i)
      }
    }
  })
  return errors
}

function normalizeAnswer(question: SurveyQuestion, input: SurveyAnswer): { answer?: SurveyAnswer; error?: string } {
  const id = question.id
  switch (question.type) {
    case 'singleChoice':
    case 'multipleChoice': {
      const allowed = new Set((question.options ?? []).map((o) => o.value))
      const choices = Array.from(new Set(input.choices ?? []))
      if (choices.length === 0) return {}
      if (question.type === 'singleChoice' && choices.length > 1) return { error: `${id}: choose one option` }
      const unknown = choices.find((c) => !allowed.has(c))
      if (unknown !== undefined) return { error: `${id}: "${unknown}" is not an option` }
      return { answer: { questionId: id, choices } }
    }
    case 'rating': {
      if (input.rating == null) return {}
      const { min, max } = getRatingScale(question)
      if (!Number.isInteger(input.rating) || input.rating < min || input.rating > max) {
        return { error: `${id}: the rating must be a whole number from ${min} to ${max}` }
      }
      return { answer: { questionId: id, rating: input.rating } }
    }
    case 'text': {
      const text = (input.text ?? '').trim()
      if (!text) return {}
      if (text.length > SURVEY_LIMITS.text) return { error: `${id}: at most ${SURVEY_LIMITS.text} characters` }
      return { answer: { questionId: id, text } }
    }
    default:
      return { error: `${id}: unknown question type` }
  }
}

/**
 * Checks one submission against the survey's questions. Returns the answers to store, in question
 * order, trimmed, with only the value field that the question type uses. Empty answers to optional
 * questions are dropped. Any error rejects the whole submission.
 */
export function validateSurveyAnswers(
  questions: SurveyQuestion[],
  input: SurveyAnswer[]
): { answers: SurveyAnswer[]; errors: string[] } {
  const errors: string[] = []
  const byId = new Map<string, SurveyAnswer>()
  for (const answer of input ?? []) {
    if (!questions.some((q) => q.id === answer?.questionId)) {
      errors.push(`unknown question "${answer?.questionId}"`)
    } else if (byId.has(answer.questionId)) {
      errors.push(`${answer.questionId}: answered twice`)
    } else {
      byId.set(answer.questionId, answer)
    }
  }

  const answers: SurveyAnswer[] = []
  for (const question of questions) {
    const given = byId.get(question.id)
    const { answer, error } = given ? normalizeAnswer(question, given) : {}
    if (error) errors.push(error)
    else if (answer) answers.push(answer)
    else if (question.required) errors.push(`${question.id}: an answer is required`)
  }
  return { answers: errors.length ? [] : answers, errors }
}

/** The answer as a HubSpot property value. Multiple choices use HubSpot's `;` separator. */
export function toHubspotValue(answer: SurveyAnswer): string | undefined {
  if (answer.choices?.length) return answer.choices.join(';')
  if (answer.rating != null) return String(answer.rating)
  if (answer.text) return answer.text
  return undefined
}

/** The HubSpot contact properties to set: one per answered question that has a mapped property. */
export function toHubspotProperties(questions: SurveyQuestion[], answers: SurveyAnswer[]): Record<string, string> {
  const properties: Record<string, string> = {}
  for (const answer of answers) {
    const property = questions.find((q) => q.id === answer.questionId)?.hubspotProperty
    const value = toHubspotValue(answer)
    // Checked again at write time: a definition saved before a property joined the list must not write it.
    if (property && value !== undefined && !RESERVED_HUBSPOT_PROPERTIES.has(property)) properties[property] = value
  }
  return properties
}

/**
 * Why a survey with responses cannot be deleted (#25660), or undefined when it has none. The API
 * error and the admin tooltip both use it, so the wording stays the same.
 */
export function surveyDeleteBlockedReason(responses: number | null | undefined): string | undefined {
  if (!responses || responses <= 0) return undefined
  return `It has ${responses} response${responses === 1 ? '' : 's'}, so it cannot be deleted. Its results stay available.`
}
